import React, { FC, useCallback, useMemo, useState } from 'react';

import { UseFormReturn } from 'react-hook-form';

import { FontAwesomeIcon } from '@appcharge/shared-ui';

import Checkbox from 'components/Checkbox/Checkbox';
import { createNumberInputHandlers } from '../../PopupsForm.utils';

import { PopUpProductDetails, ProductsSequenceProduct } from 'common/contracts';
import { EProductPriority } from 'constants/enums';
import { PRODUCT_PRIORITY_OPTIONS } from 'views/Offers/Offer.consts';

import Button from 'components/Button/Button';
import DataTable from 'components/DataTableComponent/DataTable';
import Input from 'components/Input/Input';
import InputSelectDropdown, {
  InputSelectDropdownOption
} from 'components/InputSelectDropdown/InputSelectDropdown';
import RichText from 'components/RichText/RichText';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from 'components/ui';
import { PopupsFormFields } from '../../PopupsForm.consts';
import { ProductEntry } from '../../hooks/usePopupProducts';
import { MAX_PRODUCTS_PER_SEQUENCE } from '../../hooks/usePopupProducts.utils';
import { useProductTableColumns } from '../../hooks/useProductTableColumns';

type ProductsFieldsProps = {
  form: UseFormReturn<PopupsFormFields>;
  isSingleProductMode: boolean;
  showStartOver?: boolean;
  formatProductQuantity: any;
  products: any;
  productsList: ProductEntry[];
  addProduct: (product: ProductsSequenceProduct) => void;
  deleteProduct: (id: string) => void;
  updateProduct: (id: string, product: ProductsSequenceProduct) => void;
};

const ProductsSection: FC<ProductsFieldsProps> = ({
  form,
  isSingleProductMode,
  showStartOver = false,
  formatProductQuantity,
  products,
  productsList,
  addProduct,
  deleteProduct,
  updateProduct
}) => {
  const [editingProductId, setEditingProductId] = useState<string | null>(null);

  const currentProductName = form.watch('productName');
  const currentQuantity = form.watch('quantity');

  const productOptions = useMemo(() => {
    if (products.isLoading || !products.data) return [];
    return products.data.result.map((product: PopUpProductDetails) => ({
      label: product.displayName || product.name,
      value: product.productId,
      image: product.images?.[0]?.url
    }));
  }, [products.data, products.isLoading]);

  const handleAddProduct = useCallback(async () => {
    const productId = form.getValues('productName');
    const quantity = form.getValues('quantity');
    const priority = form.getValues('productPriority') || EProductPriority.SUB;

    if (!productId || quantity < 1) return;

    const selectedProduct = products.data?.result.find(
      (product: PopUpProductDetails) => product.productId === productId
    );

    if (!selectedProduct) return;

    const formattedProductQuantity = await formatProductQuantity.mutateAsync({
      quantity: quantity.toString(),
      productType: selectedProduct.type
    });

    const newProduct = {
      productId: selectedProduct.productId,
      publisherProductId: selectedProduct.publisherProductId,
      quantity,
      priority,
      displayName: selectedProduct.displayName || selectedProduct.name,
      image: selectedProduct.images?.[0]?.url || '',
      quantityDisplay: formattedProductQuantity.data
    };

    if (editingProductId) {
      updateProduct(editingProductId, newProduct);
      form.setValue(
        'products',
        productsList.map((p) =>
          p.id === editingProductId ? { ...p, ...newProduct } : p
        ),
        { shouldDirty: true, shouldValidate: true }
      );
    } else {
      const existsAlready =
        isSingleProductMode && productsList.some((p) => p.productId === productId);

      if (existsAlready) {
        const existing = productsList.find((p) => p.productId === productId);
        if (existing) {
          updateProduct(existing.id, newProduct);
        }
        form.setValue(
          'products',
          productsList.map((p) =>
            p.productId === productId ? { ...p, ...newProduct } : p
          ),
          { shouldDirty: true, shouldValidate: true }
        );
      } else {
        addProduct(newProduct);
        form.setValue('products', [...productsList, newProduct], {
          shouldDirty: true,
          shouldValidate: true
        });
      }
    }

    form.resetField('productName');
    form.setValue('quantity', 1, { shouldDirty: true, shouldValidate: true });
    form.setValue('productPriority', EProductPriority.SUB, { shouldDirty: false });
    setEditingProductId(null);
  }, [
    form,
    products.data?.result,
    addProduct,
    updateProduct,
    productsList,
    formatProductQuantity,
    editingProductId,
    isSingleProductMode
  ]);

  const handleEditProduct = useCallback(
    async (id: string) => {
      setEditingProductId(id);
      const product = productsList.find((p) => p.id === id);
      if (!product) return;

      const selectedProduct = products.data?.result.find(
        (p: PopUpProductDetails) => p.productId === product.productId
      );

      if (!selectedProduct) return;

      const formattedProductQuantity = await formatProductQuantity.mutateAsync({
        quantity: String(product.quantity ?? 0),
        productType: selectedProduct.type
      });

      form.setValue('productName', product.productId, {
        shouldDirty: true,
        shouldValidate: true
      });
      form.setValue('quantity', Number(product.quantity), {
        shouldDirty: true,
        shouldValidate: true
      });
      form.setValue(
        'productPriority',
        (product.priority as EProductPriority) || EProductPriority.SUB,
        { shouldDirty: true, shouldValidate: true }
      );

      updateProduct(id, {
        ...product,
        quantityDisplay: formattedProductQuantity.data
      });
    },
    [
      productsList,
      form,
      products.data?.result,
      updateProduct,
      formatProductQuantity
    ]
  );

  const handleDeleteProduct = useCallback(
    (id: string) => {
      deleteProduct(id);
      const updated = productsList.filter((p) => p.id !== id);
      form.setValue('products', updated, {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [deleteProduct, productsList, form]
  );

  const COLUMNS = useProductTableColumns(
    handleDeleteProduct,
    handleEditProduct
  );

  const isProductDropdownDisabled =
    !editingProductId &&
    ((isSingleProductMode && productsList.length > 0) ||
      productsList.length >= MAX_PRODUCTS_PER_SEQUENCE);

  return (
    <div>
      <div className="mb-5">
        <RichText
          title="Products"
          description="Add product to your offer"
          className="mb-2.5"
        />
        {showStartOver && (
          <FormField
            control={form.control}
            name="startOver"
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <Checkbox
                    dataTestId="dashPopupStartOver"
                    checked={value}
                    onCheckedChange={onChange}
                    label="Start over"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )}
      </div>
      <div className="grid grid-cols-3 gap-5 mb-5">
        <FormField
          control={form.control}
          name="productName"
          render={({ field: { value, onChange } }) => (
            <FormItem>
              <FormControl>
                <InputSelectDropdown
                  listDataTestId="dashPopupProductList"
                  disabled={isProductDropdownDisabled}
                  key={value}
                  placeholder="Select product"
                  label="Product name"
                  defaultSelected={productOptions.find(
                    (option: InputSelectDropdownOption) =>
                      option.value === currentProductName
                  )}
                  options={productOptions}
                  onOptionChange={onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="productPriority"
          render={({ field: { value, onChange } }) => (
            <FormItem>
              <FormControl>
                <InputSelectDropdown
                  label="Products Type"
                  placeholder="Select Priority"
                  options={PRODUCT_PRIORITY_OPTIONS}
                  defaultSelected={PRODUCT_PRIORITY_OPTIONS.find(
                    (option) => option.value === value
                  )}
                  onOptionChange={onChange}
                  listDataTestId="popupProductPriorityList"
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="quantity"
          render={({ field: { value, onChange, onBlur } }) => (
            <FormItem>
              <FormControl>
                <Input
                  name="quantity"
                  disabled={!currentProductName}
                  label="Quantity"
                  type="number"
                  min={1}
                  value={value}
                  {...createNumberInputHandlers(onChange, 1)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <Button
        onClick={handleAddProduct}
        disabled={!currentProductName || typeof currentQuantity !== 'number'}
        className="w-full"
        leftIcon={<FontAwesomeIcon icon="fa-regular fa-circle-plus" />}
        variant="secondary"
        type="button"
      >
        Add Product
      </Button>
      {productsList.length > 0 && (
        <DataTable
          className="mt-9"
          data={productsList}
          columns={COLUMNS}
          showPagination={false}
        />
      )}
    </div>
  );
};

export { ProductsSection };
